import { ENV_CONFIG } from '@/constants/envs/constants'
import { ChainId } from '@/lib/chain'
import { Native, SerializedCryptoType } from '@/lib/cryptoCurrency'
import { SerializedFait } from '@/lib/fiatCurrency/zod'
import { getActiveChainId } from '@/utils'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Address } from 'viem'

interface ICurrentChainResult {
  chainId: ChainId
  token: SerializedCryptoType
}

const getCurrentChain = (): ICurrentChainResult => {
  const storedChainId = getActiveChainId()
  if (storedChainId) {
    const storedChainIdNum = Number(storedChainId) as ChainId

    if (ENV_CONFIG.supportedChains.includes(storedChainIdNum)) {
      return {
        chainId: storedChainIdNum as ChainId,
        token: Native.onChain(storedChainIdNum).serialize()
      }
    }
  }
  return {
    chainId: ENV_CONFIG.defaultChainId,
    token: Native.onChain(ENV_CONFIG.defaultChainId).serialize()
  }
}

export interface ISellCryptoState {
  readonly chainId: ChainId
  readonly fromToken: SerializedCryptoType
  readonly fromTokenAmount: string
  readonly toFiatCurrency: SerializedFait
  readonly toFiatCurrencyAmount: string
  readonly senderAddress: Address | ''
  readonly cryptoMinLimit: string
  readonly cryptoMaxLimit: string
  readonly objectiveOfTrading: string | null
}

const initialChainData = getCurrentChain()

const initialState: ISellCryptoState = {
  chainId: initialChainData.chainId,
  fromToken: initialChainData.token,
  fromTokenAmount: '0.0005',
  toFiatCurrency: {
    name: 'Thai baht',
    symbol: 'THB',
    decimals: 2
  },
  toFiatCurrencyAmount: '0',
  senderAddress: '',
  cryptoMinLimit: '0',
  cryptoMaxLimit: '0',
  objectiveOfTrading: null
}

const sellCryptoSlice = createSlice({
  name: 'sellCrypto',
  initialState,
  reducers: {
    setChainId(state, action: PayloadAction<ChainId>) {
      state.chainId = action.payload
    },
    setFromToken(state, action: PayloadAction<SerializedCryptoType>) {
      state.fromToken = action.payload
    },
    setFromTokenAmount(state, action: PayloadAction<string>) {
      state.fromTokenAmount = action.payload
    },
    setToFiatCurrency(state, action: PayloadAction<SerializedFait>) {
      state.toFiatCurrency = action.payload
    },
    setToFiatCurrencyAmount(state, action: PayloadAction<string>) {
      state.toFiatCurrencyAmount = action.payload
    },
    setSenderAddress(state, action: PayloadAction<Address | ''>) {
      state.senderAddress = action.payload
    },
    setCryptoLimit(
      state,
      action: PayloadAction<{ cryptoMinLimit: string; cryptoMaxLimit: string }>
    ) {
      state.cryptoMinLimit = action.payload.cryptoMinLimit
      state.cryptoMaxLimit = action.payload.cryptoMaxLimit
    },
    setObjectiveOfTrading(state, action: PayloadAction<string>) {
      state.objectiveOfTrading = action.payload
    },
    clearObjectiveOfTrading(state) {
      state.objectiveOfTrading = null
    },
    clearSenderAddress(state) {
      state.senderAddress = initialState.senderAddress
    }
  }
})

export const {
  setChainId,
  setFromToken,
  setFromTokenAmount,
  setToFiatCurrency,
  setToFiatCurrencyAmount,
  setSenderAddress,
  setCryptoLimit,
  setObjectiveOfTrading,
  clearObjectiveOfTrading,
  clearSenderAddress
} = sellCryptoSlice.actions

export default sellCryptoSlice.reducer
